// 외부 링크 사진 선택 규칙·검사 (WP5 4단계 §7.3)
// 최종 형식 판별은 서버가 하며, 여기는 사전 안내용이다.

export interface PhotoRule {
  maxCount: number;
  maxBytes: number;
  allowedExt: readonly string[];
}

const MB = 1024 * 1024;

/** 상차사진(Planner B6): 5장, 장당 20MB, GIF 불가 */
export const LOAD_PHOTO_RULE: PhotoRule = {
  maxCount: 5,
  maxBytes: 20 * MB,
  allowedExt: ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'],
};

/** 하차(입고)사진(WorkLog): 5장, 장당 20MB, GIF 허용 */
export const UNLOAD_PHOTO_RULE: PhotoRule = {
  maxCount: 5,
  maxBytes: 20 * MB,
  allowedExt: ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif', 'gif'],
};

export type PhotoRejectReason = 'TYPE' | 'SIZE' | 'COUNT';

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  if (i < 0 || i === name.length - 1) return '';
  return name.slice(i + 1).toLowerCase();
}

/**
 * 확장자 → 장수 → 용량 순으로 검사한다(한 파일은 앞선 사유 하나만 기록, 입력 순서 유지).
 * currentCount: 이미 담긴 사진 수. 남은 칸 = maxCount - currentCount.
 */
export function pickPhotos(
  files: File[],
  currentCount: number,
  rule: PhotoRule,
): { accepted: File[]; rejected: { file: File; reason: PhotoRejectReason }[] } {
  const accepted: File[] = [];
  const rejected: { file: File; reason: PhotoRejectReason }[] = [];
  let remaining = Math.max(0, rule.maxCount - currentCount);
  for (const file of files) {
    const ext = extOf(file.name);
    if (!ext || !rule.allowedExt.includes(ext)) {
      rejected.push({ file, reason: 'TYPE' });
    } else if (remaining <= 0) {
      rejected.push({ file, reason: 'COUNT' });
    } else if (file.size > rule.maxBytes) {
      rejected.push({ file, reason: 'SIZE' });
    } else {
      accepted.push(file);
      remaining -= 1;
    }
  }
  return { accepted, rejected };
}

/** 거절 사유 안내 문구. 사유가 여럿이면 TYPE → SIZE → COUNT 순 한 줄씩. 거절이 없으면 null. */
export function rejectNotice(rejected: { reason: PhotoRejectReason }[], rule: PhotoRule): string | null {
  if (rejected.length === 0) return null;
  const has = (r: PhotoRejectReason) => rejected.some((x) => x.reason === r);
  const lines: string[] = [];
  if (has('TYPE')) {
    lines.push(`사진 파일만 올릴 수 있어요. (${rule.allowedExt.map((e) => e.toUpperCase()).join(', ')})`);
  }
  if (has('SIZE')) {
    lines.push(`사진은 한 장당 ${Math.round(rule.maxBytes / (1024 * 1024))}MB까지 올릴 수 있어요.`);
  }
  if (has('COUNT')) {
    lines.push(`사진은 최대 ${rule.maxCount}장까지 올릴 수 있어요.`);
  }
  return lines.join('\n');
}
